import type { SendMessageOptions, Citation, ChatMessage, ChatSuggestion, ChatLevelUp } from '@/types/chat';
import { MessageRole } from '@/constants';
import { api, CHAT_TAG } from '@/api';
import { CHAT_DOCUMENTS_TAG } from '@/api/constants';
import { generateChatTitleApi } from '@/api/chat/generateChatTitle';
import { streamChatResponse, type StreamingChatCallbacks, type FinalResultArgs } from '@/utils/streamingChat';
import { chatMessagesCache, getChatKey } from '@/hooks/chatMessagesCache';
import { clearPendingMessageForChat, setPendingMessageForChat, setLastResponseAt } from '@/store/slices/chat/chat';
import type { AppDispatch } from '@/store/store';
import { globalToastService } from '@repo/atomic-components';
import { getToastDetailForChatRunError } from '@/utils/chatRunErrorToast';
import type { ChatProcessingStep } from '@/types/chat';
import { STREAMING_CURSOR_CHAR } from '@/constants/chat';
import { hideUnterminatedCitationMarker, htmlToPlainText, isUuidLike } from '@/utils';
import { resolveExistingDocumentIds } from './resolveExistingDocumentIds';
import { shouldAskForFeedback } from './feedbackReminderChance';

export const buildRequestPayload = (chatId: string, userMessage: ChatMessage, options?: SendMessageOptions) => {
  const threadId = chatId || crypto.randomUUID();

  return {
    chatId: chatId || threadId,
    existingDocumentIds: options?.existingDocumentIds ?? [],
    newlySelectedHistoryRowIds: (options?.newlySelectedHistoryRowIds ?? []).filter(isUuidLike),
    messageType: options?.messageType ?? 'USER_MESSAGE',
    userMessage: userMessage.content,
  };
};

const showChatErrorToast = (detail: string) => {
  globalToastService.show({
    closable: true,
    severity: 'error',
    detail,
    life: 6000,
  });
};

export interface StreamSendResult {
  runFinished: boolean;
}

/**
 * Sends a chat message and streams the response into `chatMessagesCache` — the single
 * source of truth for chat streaming, used by `useChatMessages` and `chatListener`.
 */
export async function streamSendChatMessage(args: {
  dispatch: AppDispatch;
  chatId: string;
  content: string;
  options?: SendMessageOptions;
}): Promise<StreamSendResult> {
  const { dispatch, chatId, content, options } = args;
  const chatKey = getChatKey(chatId);

  if (!content.trim()) return { runFinished: false };
  if (chatMessagesCache.isLoading(chatKey)) return { runFinished: false };

  const currentMessages = chatMessagesCache.getMessages(chatId);
  // A failed prompt got no title, so the first answered one still names the chat.
  const shouldGenerateTitle = currentMessages.every((message) => message.status === 'FAILED');
  const attachmentDocs = options?.messageAttachmentDocuments;

  const userMessage: ChatMessage = {
    id: crypto.randomUUID(),
    messageId: '',
    role: MessageRole.User,
    content: content.trim(),
    createdAt: new Date(),
    ...(attachmentDocs?.length ? { attachedDocuments: attachmentDocs } : {}),
  };

  const assistantMessageId = crypto.randomUUID();
  const assistantMessage: ChatMessage = {
    id: assistantMessageId,
    messageId: assistantMessageId,
    role: MessageRole.Assistant,
    content: '',
    createdAt: new Date(),
    isOptimistic: true,
  };

  chatMessagesCache.setMessages(chatId, [...currentMessages, userMessage, assistantMessage]);
  chatMessagesCache.setLoading(chatId, true);
  chatMessagesCache.setError(null);

  const abortController = new AbortController();
  chatMessagesCache.setAbortController(chatId, abortController);

  if (chatId) {
    dispatch(
      setPendingMessageForChat({
        chatId,
        userPrompt: userMessage.content,
        attachedDocuments: attachmentDocs,
        isStreaming: true,
      }),
    );
  }

  let runFinishedSuccessfully = false;
  // Tracks whether we've received the assistant's final output. If true, a later
  // RUN_ERROR is a backend persistence failure and we should keep the message.
  let finalResultReceived = false;
  let nextStepsFailureToastShown = false;
  let runFailed = false;

  const showNextStepsFailedToast = () => {
    if (nextStepsFailureToastShown) return;
    nextStepsFailureToastShown = true;
    showChatErrorToast('Suggested Next steps failed to load');
  };

  /** No response came back: the prompt stays in the chat, marked failed, and the empty response goes. */
  const failRun = (errorMessage: string) => {
    if (runFailed) return;
    runFailed = true;
    showChatErrorToast(getToastDetailForChatRunError(errorMessage));
    chatMessagesCache.setError(errorMessage);
    chatMessagesCache.updateMessages(chatId, (prev) =>
      prev
        .filter((msg) => msg.id !== assistantMessageId)
        .map((msg): ChatMessage => (msg.id === userMessage.id ? { ...msg, status: 'FAILED' } : msg)),
    );
    // Refetch so the BE's copy of the prompt, with its real id, replaces this one.
    if (chatId) {
      dispatch(api.util.invalidateTags([{ type: CHAT_TAG, id: chatId }]));
    }
  };

  const callbacks: StreamingChatCallbacks = {
    onFinalResult: (finalArgs: FinalResultArgs) => {
      finalResultReceived = true;
      // New streaming protocol ends with `result` (no explicit RUN_FINISHED).
      // Treat the final result as a successful finish so the input is cleared.
      runFinishedSuccessfully = true;
      // Prefer `internal_document_id` (the real document id); fall back to `document_id` only when it's a UUID.
      const citations: Record<string, Citation> = {};
      (finalArgs.citation_index ?? []).forEach((entry) => {
        const resolvedDocumentId =
          entry.internal_document_id ?? (isUuidLike(entry.document_id) ? entry.document_id : undefined);
        if (!resolvedDocumentId) return;
        citations[String(entry.citation_id)] = {
          documentId: resolvedDocumentId,
          pageNumber: 0,
          extract: htmlToPlainText(entry.quote),
        };
      });
      chatMessagesCache.updateMessages(chatId, (prev) =>
        prev.map((msg) =>
          msg.id === assistantMessageId
            ? { ...msg, content: finalArgs.full_output, citations, finalResultReceived: true }
            : msg,
        ),
      );

      dispatch(setLastResponseAt());

      // Keep cache consistent with the older RUN_FINISHED flow.
      dispatch(
        api.util.invalidateTags([
          { type: CHAT_TAG, id: chatId },
          ...(chatId ? [{ type: CHAT_DOCUMENTS_TAG, id: chatId } as const] : []),
        ]),
      );
    },
    onFinalResultPartial: ({ full_output, partial }) => {
      // Half-arrived citation markers are withheld so a partial id never flashes mid-response;
      // complete ones stay in and render as placeholder pills until `result` numbers them.
      const display = partial ? `${hideUnterminatedCitationMarker(full_output)}${STREAMING_CURSOR_CHAR}` : full_output;
      chatMessagesCache.updateMessages(chatId, (prev) =>
        prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: display } : msg)),
      );
    },
    onDraftSaved: (event) => {
      if (event.type === 'draft') {
        const value = event.value as {
          draftId?: string;
          draftName?: string;
          draftVersionId?: string;
          title?: string;
          generatedDocumentId?: string;
        };
        // Accept both legacy (draftId/draftName) and new (draftVersionId/title) payload shapes.
        const resolvedVersionId = value.draftVersionId ?? value.draftId;
        const resolvedTitle = value.title ?? value.draftName;
        if (!resolvedVersionId || !resolvedTitle) return;

        chatMessagesCache.updateMessages(chatId, (prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i -= 1) {
            if (next[i].role === MessageRole.Assistant) {
              next[i] = {
                ...next[i],
                drafts: [
                  {
                    draftVersionId: resolvedVersionId,
                    title: resolvedTitle,
                    generatedDocumentId: value.generatedDocumentId,
                  },
                ],
              };
              break;
            }
          }
          return next;
        });
        return;
      }
    },

    onToolStart: (event) => {
      const toolName = String(event.tool_name ?? '');
      if (!toolName) return;

      const label = typeof event.label === 'string' && event.label.trim() ? event.label.trim() : toolName;
      const newStep: ChatProcessingStep = {
        id: crypto.randomUUID(),
        toolName,
        label,
        status: 'in_progress',
        startedAt: Date.now(),
      };

      chatMessagesCache.updateMessages(chatId, (prev) =>
        prev.map((msg) => {
          if (msg.id !== assistantMessageId) return msg;
          const steps = msg.processingSteps ?? [];
          return { ...msg, processingSteps: [...steps, newStep] };
        }),
      );
    },
    onToolEnd: (event) => {
      const toolName = String(event.tool_name ?? '');
      if (!toolName) return;

      chatMessagesCache.updateMessages(chatId, (prev) =>
        prev.map((msg) => {
          if (msg.id !== assistantMessageId) return msg;
          const steps = msg.processingSteps ?? [];
          const idx = steps.findIndex((s) => s.toolName === toolName && s.status === 'in_progress');
          if (idx === -1) return msg;

          const next = [...steps];
          next[idx] = { ...next[idx], status: 'completed', endedAt: Date.now() };
          return { ...msg, processingSteps: next };
        }),
      );
    },
    onSuggestions: (event) => {
      const suggestions = Array.isArray(event.value) ? (event.value as ChatSuggestion[]) : [];

      // We only show Next steps when we have a full set.
      if (suggestions.length < 3) {
        showNextStepsFailedToast();
        chatMessagesCache.updateMessages(chatId, (prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i -= 1) {
            if (next[i].role === MessageRole.Assistant) {
              next[i] = { ...next[i], suggestions: [] };
              break;
            }
          }
          return next;
        });
        return;
      }

      chatMessagesCache.updateMessages(chatId, (prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i].role === MessageRole.Assistant) {
            next[i] = { ...next[i], suggestions };
            break;
          }
        }
        return next;
      });
    },
    onLevelUp: (event) => {
      const levelUp = event.value as ChatLevelUp[];
      if (levelUp.length === 0) return;

      chatMessagesCache.updateMessages(chatId, (prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i].role === MessageRole.Assistant) {
            next[i] = { ...next[i], levelUp };
            break;
          }
        }
        return next;
      });
    },
    onError: (err) => {
      console.error('Streaming error:', err);

      if (finalResultReceived) {
        showChatErrorToast(getToastDetailForChatRunError(err.message));
        return;
      }
      failRun(err.message);
    },
  };

  try {
    // Resolved here rather than before the optimistic message so the prompt still renders
    // instantly; the run only starts once the chat's real document list is known.
    const existingDocumentIds = await resolveExistingDocumentIds({
      dispatch,
      chatId,
      clientDocumentIds: options?.existingDocumentIds ?? [],
    });

    await streamChatResponse(
      `${import.meta.env.VITE_FLETCH_API_URL}/fletch/chat/send`,
      buildRequestPayload(chatId, userMessage, { ...options, existingDocumentIds }),
      callbacks,
      abortController.signal,
    );

    if (!finalResultReceived && !runFailed && !abortController.signal.aborted) {
      console.error('Chat response stream closed without a result');
      failRun('');
    }

    // The stream only closes after the next steps, so the whole response is done here.
    if (runFinishedSuccessfully && shouldAskForFeedback()) {
      chatMessagesCache.updateMessages(chatId, (prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i -= 1) {
          if (next[i].role === MessageRole.Assistant) {
            next[i] = { ...next[i], askForFeedback: true };
            break;
          }
        }
        return next;
      });
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { runFinished: runFinishedSuccessfully };
    }
    const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
    console.error('Error sending message:', err);
    if (finalResultReceived) {
      showChatErrorToast(getToastDetailForChatRunError(errorMessage));
    } else {
      failRun(errorMessage);
    }
  } finally {
    chatMessagesCache.setLoading(chatId, false);
    chatMessagesCache.setAbortController(chatId, undefined);
    if (chatId) {
      dispatch(clearPendingMessageForChat({ chatId }));
    }
  }

  // Title generation must not depend on ChatPage being mounted.
  // If this was the first message in a brand-new chat, generate title after the run finishes.
  if (runFinishedSuccessfully && shouldGenerateTitle && chatId) {
    try {
      await dispatch(generateChatTitleApi.endpoints.generateChatTitle.initiate({ chatId }, { track: false })).unwrap();
    } catch (err) {
      // Non-fatal.
      console.error('Failed to generate chat title:', err);
    }
  }

  return { runFinished: runFinishedSuccessfully };
}
